/**
 * Cliente HTTP mínimo basado en fetch.
 * Inyecta Bearer token desde el auth-store automáticamente.
 */
import { useAuthStore } from "@/lib/auth-store";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export class ApiError extends Error {
  status: number;
  details: unknown;

  constructor(message: string, status: number, details: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  form?: URLSearchParams;
  skipAuth?: boolean;
  /** Interno: evita reintentar el refresh de forma recursiva. */
  _retried?: boolean;
}

// ── Refresh de sesión ────────────────────────────────────────────────────────
// Un solo refresh en vuelo a la vez (varias queries que reciben 401 a la par
// comparten la misma promesa). Si el refresh falla, se cierra sesión y se
// redirige al login — antes la app quedaba inutilizable al expirar el token.
let refreshInFlight: Promise<boolean> | null = null;

function redirectToLogin(): void {
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

async function refreshSession(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { access_token: string; refresh_token: string };
    useAuthStore.getState().setSession({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    });
    return true;
  } catch {
    return false;
  }
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, form, skipAuth, headers, _retried, ...rest } = opts;
  const h = new Headers(headers);

  let payload: BodyInit | undefined;
  if (form) {
    h.set("content-type", "application/x-www-form-urlencoded");
    payload = form;
  } else if (body !== undefined) {
    h.set("content-type", "application/json");
    payload = JSON.stringify(body);
  }

  if (!skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) h.set("authorization", `Bearer ${token}`);
  }

  const init: RequestInit = { ...rest, headers: h };
  if (payload !== undefined) init.body = payload;
  const res = await fetch(`${API_BASE_URL}${path}`, init);

  // 401 en una request autenticada → intentar refrescar la sesión una vez y reintentar.
  if (res.status === 401 && !skipAuth && !_retried && path !== "/auth/refresh") {
    refreshInFlight = refreshInFlight ?? refreshSession();
    const ok = await refreshInFlight;
    refreshInFlight = null;
    if (ok) {
      return apiRequest<T>(path, { ...opts, _retried: true });
    }
    // No se pudo refrescar → cerrar sesión y mandar al login.
    useAuthStore.getState().logout();
    redirectToLogin();
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const detail =
      typeof data === "object" && data !== null && "detail" in data
        ? String((data as Record<string, unknown>).detail)
        : `HTTP ${res.status}`;
    throw new ApiError(detail, res.status, data);
  }

  return data as T;
}
